/**
 * The client's starting point, worked out from the cleaned parcels: what they ship and what it
 * costs today. Every saving in the report is measured against these figures.
 * The measures themselves come from the engine (packages/engine/src/baseline.ts).
 */
import { annualise, baseline, type AirBaseline, type Parcel, type ProductSpec } from "@ryoma/engine";

export interface ServiceLine {
  service: string;
  parcels: number;
  spendPence: number;
  avgCostPence: number;
}

export interface Baseline {
  parcels: number;
  days: number;
  /** Parcels in an average month (30.44 days) */
  parcelsPerMonth: number;
  spendPence: number;
  /** Spend scaled to a year; null when the data covers under 28 days */
  annualSpendPence: number | null;
  avgCostPence: number;
  medianCostPence: number;
  avgWeightKg: number;
  /** Share of parcels with more than one item */
  multiItemShare: number;
  services: ServiceLine[];
  /** Empty space in single-product parcels; null without product sizes */
  air: AirBaseline | null;
}

export function computeBaseline(parcels: Parcel[], dateFrom: string | null, dateTo: string | null, products: ProductSpec[] = []): Baseline {
  const b = baseline(parcels, products);
  const n = parcels.length;
  const days = dateFrom && dateTo ? Math.round((Date.parse(`${dateTo}T12:00:00Z`) - Date.parse(`${dateFrom}T12:00:00Z`)) / 864e5) + 1 : 0;
  const multi = parcels.filter((p) => p.items.length > 1 || p.items.some((i) => i.qty > 1)).length;
  return {
    parcels: n,
    days,
    parcelsPerMonth: days > 0 ? Math.round((n / days) * 30.44) : n,
    spendPence: b.spendPence,
    annualSpendPence: annualise(b.spendPence, dateFrom, dateTo),
    avgCostPence: b.avgCostPence,
    medianCostPence: b.medianCostPence,
    avgWeightKg: b.avgWeightKg,
    multiItemShare: n ? multi / n : 0,
    services: b.byService.map((s) => ({ service: s.service, parcels: s.parcels, spendPence: s.spendPence, avgCostPence: s.avgPence })),
    air: b.air,
  };
}
